import type { ImportCell } from "../dataImport/types";
import type { LabeledValue } from "../types";

const TRUE_WORDS = ["oui", "yes", "true", "vrai", "1", "x"];
const FALSE_WORDS = ["non", "no", "false", "faux", "0"];

/** "Oui" / "Non" (or yes, true, 1, x...) as a boolean, undefined when empty or unknown. */
export const toBoolean = (cell: ImportCell): boolean | undefined => {
  if (typeof cell === "boolean") return cell;
  const text = cell == null ? "" : String(cell).trim().toLowerCase();
  if (TRUE_WORDS.includes(text)) return true;
  if (FALSE_WORDS.includes(text)) return false;
  return undefined;
};

/** A cell listing several values, separated by commas or semicolons. */
export const toList = (cell: ImportCell): string[] =>
  cell == null
    ? []
    : String(cell)
        .split(/[,;]/)
        .map((item) => item.trim())
        .filter((item) => item !== "");

/** Option matching a label or a stored value, case and accents aside. */
export const findOption = (
  text: string,
  options: LabeledValue[],
): LabeledValue | undefined => {
  const wanted = normalize(text);
  return options.find(
    (option) =>
      normalize(option.value) === wanted || normalize(option.label) === wanted,
  );
};

/** Stored value of a list cell, from its label or value; undefined when unknown. */
export const toChoice = (
  cell: ImportCell,
  options: LabeledValue[],
): string | undefined => {
  const text = cell == null ? "" : String(cell).trim();
  return text === "" ? undefined : findOption(text, options)?.value;
};

/** Stored values of a multi-choice cell ("Séminaire, Cocktail"), unknown items dropped. */
export const toConfiguredValues = (
  cell: ImportCell,
  options: LabeledValue[],
): string[] =>
  toList(cell)
    .map((item) => findOption(item, options)?.value)
    .filter((value): value is string => value !== undefined);

/** Labels of stored values, joined for a single cell. */
export const toLabels = (
  values: string | string[] | null | undefined,
  options: LabeledValue[],
): string | undefined => {
  if (values == null) return undefined;
  const list = Array.isArray(values) ? values : [values];
  if (list.length === 0) return undefined;
  return list
    .map(
      (value) =>
        options.find((option) => option.value === value)?.label ?? value,
    )
    .join(", ");
};

/** A stored ISO date as a Date for Excel, undefined when empty or invalid. */
export const toExcelDate = (
  value: string | null | undefined,
): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/** A "YYYY-MM-DD" day from a date read in Excel, using the day Excel shows. */
export const toDayString = (date: Date): string =>
  [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");

/**
 * A day typed or read in a sheet, as "YYYY-MM-DD": accepts 2026-10-20,
 * 20.10.2026 and 20/10/2026 (day first, as in Switzerland). Impossible days
 * such as 31.02.2026 are refused rather than rolled over.
 */
export const toDay = (cell: ImportCell): string | undefined => {
  if (cell == null) return undefined;
  const text = String(cell).trim();
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  const swiss = text.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  const parts = iso
    ? [iso[1], iso[2], iso[3]]
    : swiss
      ? [swiss[3], swiss[2], swiss[1]]
      : undefined;
  if (!parts) return undefined;
  const [year, month, day] = parts;
  const result = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${result}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && toDayString(date) === result
    ? result
    : undefined;
};

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
